import { useState, useEffect } from 'react'
import { Upload, X, File, RefreshCw, Info } from 'lucide-react'
import { useEvidenceStore } from '../store/evidenceStore'
import { useAuthStore } from '../store/authStore'

const UploadEvidenceForm = () => {
  const [selectedDimension, setSelectedDimension] = useState('')
  const [selectedCriterio, setSelectedCriterio] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [dragActive, setDragActive] = useState(false)
  const [missingEvidenceText, setMissingEvidenceText] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [pertinencia, setPertinencia] = useState('')
  const [suficiencia, setSuficiencia] = useState('')
  
  const { 
    uploadEvidence, 
    formStructure, 
    fetchFormStructure, 
    refreshFormStructure,
    isLoading, 
    isLoadingStructure, 
    error 
  } = useEvidenceStore()
  const { token, user } = useAuthStore()

  // Cargar estructura del formulario al montar el componente
  useEffect(() => {
    if (!formStructure) {
      fetchFormStructure(false, user?.email)
    }
  }, [formStructure, fetchFormStructure, user?.email])

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFiles = Array.from(e.dataTransfer.files)
      setSelectedFiles(prev => [...prev, ...newFiles])
    }
  }

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setSelectedFiles(prev => [...prev, ...newFiles])
    }
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validar que tengamos archivos O texto de evidencia faltante
    if (!selectedDimension || !selectedCriterio || (selectedFiles.length === 0 && !missingEvidenceText.trim())) {
      return
    }

    // Validar campos obligatorios de Pertinencia y Suficiencia
    if (!pertinencia.trim() || !suficiencia.trim()) {
      return
    }

    const result = await uploadEvidence(
      token, 
      selectedFiles, 
      selectedDimension, 
      selectedCriterio,
      selectedYear,
      missingEvidenceText.trim(),
      pertinencia.trim(),
      suficiencia.trim()
    )
    
    if (result.success) {
      setSelectedDimension('')
      setSelectedCriterio('')
      setSelectedFiles([])
      setMissingEvidenceText('')
      setSelectedYear(new Date().getFullYear().toString())
      setPertinencia('')
      setSuficiencia('')
    }
  }

  // Obtener criterios de la dimensión seleccionada
  const criterios = selectedDimension && formStructure 
    ? formStructure[selectedDimension] || [] 
    : []

  // Obtener información del criterio seleccionado
  const selectedCriterioInfo = criterios.find(c => 
    typeof c === 'object' ? c.nombre === selectedCriterio : c === selectedCriterio
  )

  // Función para refrescar la estructura (solo admin)
  const handleRefreshStructure = async () => {
    if (user?.rol === 'admin') {
      await refreshFormStructure(token)
    } else {
      await fetchFormStructure(true, user?.email)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Subir Nueva Evidencia</h3>
        
        <button
          onClick={handleRefreshStructure}
          disabled={isLoadingStructure}
          className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 px-3 py-1 rounded border border-gray-300 hover:border-gray-400 transition-colors"
          title={user?.rol === 'admin' ? 'Actualizar estructura (Admin)' : 'Recargar estructura'}
        >
          <RefreshCw className={`h-4 w-4 ${isLoadingStructure ? 'animate-spin' : ''}`} />
          <span>Actualizar</span>
        </button>
      </div>

      {isLoadingStructure && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-800">Cargando estructura del formulario...</span>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dimensión
            </label>
            <select
              value={selectedDimension}
              onChange={(e) => {
                setSelectedDimension(e.target.value)
                setSelectedCriterio('')
              }}
              className="input-field"
              required
              disabled={isLoadingStructure}
            >
              <option value="">Seleccionar dimensión</option>
              {formStructure && Object.keys(formStructure).map(dimension => (
                <option key={dimension} value={dimension}>
                  {dimension}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Criterio
            </label>
            
            <select
              value={selectedCriterio}
              onChange={(e) => {
                setSelectedCriterio(e.target.value)
              }}
              className="input-field"
              required
              disabled={!selectedDimension || isLoadingStructure}
            >
              <option value="">Seleccionar criterio</option>
              {criterios.map((criterio, index) => {
                const criterioNombre = typeof criterio === 'object' ? criterio.nombre : criterio
                return (
                  <option key={index} value={criterioNombre}>
                    {criterioNombre}
                  </option>
                )
              })}
            </select>
          </div>
        </div>

        {/* Información del criterio - se muestra automáticamente ocupando todo el ancho */}
        {selectedCriterio && selectedCriterioInfo && (
          <div className="space-y-2">
            {selectedCriterioInfo.descripcion && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Descripción:</strong> {selectedCriterioInfo.descripcion}
                </p>
              </div>
            )}
            {selectedCriterioInfo.ejemplo && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>💡 Ejemplo:</strong> {selectedCriterioInfo.ejemplo}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Campo de año */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Año de la Evidencia
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="input-field"
            required
          >
            {Array.from({ length: 11 }, (_, i) => {
              const year = 2022 + i // Desde 2022 hasta 2032 (11 años)
              return (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              )
            })}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Selecciona el año al que corresponde esta evidencia (no el año de carga)
          </p>
        </div>

        {/* Campos obligatorios de Pertinencia y Suficiencia */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Por qué es Pertinente? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={pertinencia}
              onChange={(e) => setPertinencia(e.target.value)}
              placeholder="Explica por qué esta evidencia es pertinente para el criterio seleccionado..."
              className="input-field min-h-[100px] resize-y"
              rows={4}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Justifica la relevancia de esta evidencia para el criterio
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Por qué es Suficiente? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={suficiencia}
              onChange={(e) => setSuficiencia(e.target.value)}
              placeholder="Explica por qué esta evidencia es suficiente para demostrar el cumplimiento..."
              className="input-field min-h-[100px] resize-y"
              rows={4}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Justifica que la evidencia demuestra adecuadamente el criterio
            </p>
          </div>
        </div>

        {/* Campo de evidencia faltante */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Evidencia Faltante (Opcional)
          </label>
          <textarea
            value={missingEvidenceText}
            onChange={(e) => setMissingEvidenceText(e.target.value)}
            placeholder="Si no tienes archivos para subir, describe aquí qué evidencia falta y por qué..."
            className="input-field min-h-[80px] resize-y"
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Usa este campo cuando necesites registrar que falta una evidencia específica
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Archivos {missingEvidenceText.trim() ? '(Opcional si hay texto de evidencia faltante)' : ''}
          </label>
          
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">
              Arrastra archivos aquí o haz clic para seleccionar
            </p>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
            />
            <label
              htmlFor="file-upload"
              className="btn-secondary cursor-pointer inline-block"
            >
              Seleccionar Archivos
            </label>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">
                Archivos seleccionados ({selectedFiles.length})
              </h4>
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <File className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={
            isLoading || 
            !selectedDimension || 
            !selectedCriterio || 
            (selectedFiles.length === 0 && !missingEvidenceText.trim())
          }
          className="btn-primary w-full disabled:opacity-50"
        >
          {isLoading ? 'Procesando...' : 
           selectedFiles.length > 0 ? 'Subir Evidencias' : 
           'Registrar Evidencia Faltante'}
        </button>
      </form>
    </div>
  )
}

export default UploadEvidenceForm